import type { components } from "@sagot/api-client";
import type { SlaTarget } from "./inbox-model";

export type SlaView = components["schemas"]["SlaView"];

/** Default wait (minutes) before the team is reminded; each agent sets its own (DEC-066). */
export const SLA_DEFAULT_REMIND_MINUTES = 10;

export type SlaState =
  | { kind: "none" }
  | { kind: "waiting"; minutes: number; overdue: boolean; escalated: boolean }
  | { kind: "replied"; minutes: number; overdue: boolean };

const MINUTE_MS = 60_000;

/** The agent's own "remind the team after" wait (the server sends it with every timer). */
const remindAfter = (sla: SlaView): number => sla.reminder_minutes || SLA_DEFAULT_REMIND_MINUTES;

function minutesBetween(from: string, to: number): number {
  return Math.max(Math.floor((to - new Date(from).getTime()) / MINUTE_MS), 0);
}

/** Handoff SLA timer: live wait since the handoff, or the time it took staff to first reply. */
export function slaState(sla: SlaView, now = Date.now()): SlaState {
  if (!sla.handoff_requested_at) return { kind: "none" };
  if (sla.first_staff_reply_at) {
    const minutes = minutesBetween(sla.handoff_requested_at, new Date(sla.first_staff_reply_at).getTime());
    return { kind: "replied", minutes, overdue: minutes > remindAfter(sla) };
  }
  const minutes = minutesBetween(sla.handoff_requested_at, now);
  return { kind: "waiting", minutes, overdue: minutes > remindAfter(sla), escalated: sla.escalated_at !== null };
}

export type TargetState = { kind: "breached" } | { kind: "due"; minutes: number; soon: boolean };

/** A due time closer than this turns the chip into a warning. */
const SOON_MINUTES = 15;

/** Where a conversation stands against its SLA policy right now (the live clock wins over the server's last count). */
export function targetState(target: SlaTarget, now = Date.now()): TargetState | null {
  const left = target.due_at ? Math.ceil((new Date(target.due_at).getTime() - now) / MINUTE_MS) : target.minutes_left;
  if (target.breached || (left !== null && left <= 0)) return { kind: "breached" };
  return left === null ? null : { kind: "due", minutes: left, soon: left <= SOON_MINUTES };
}

/** "45m" / "2h 05m" / "3d 4h" compact durations for timers. */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ${String(minutes % 60).padStart(2, "0")}m`;
  return `${String(Math.floor(hours / 24))}d ${String(hours % 24)}h`;
}
